/**
 * Toast feedback for milestone crossings. Subscribes to `milestoneEvents`
 * and pops a short-lived card in the top-center for each crossing so a
 * payout never looks like it came from nowhere.
 *
 * The persistent log row that complements this toast lives inside
 * TokenMcapPanel (and is fed by the same event source via
 * `installMilestoneLogBridge`).
 */
import { LAMPORTS_PER_SOL } from "@solana/web3.js";
import React, { useEffect, useState } from "react";

import { milestoneEvents, type MilestoneCrossing } from "./milestoneEvents";

interface ActiveToast extends MilestoneCrossing {
  expiresAt: number;
}

const TOAST_MS = 5500;

function fmtSol(lamports: number): string {
  const sol = lamports / LAMPORTS_PER_SOL;
  if (sol >= 1000) return `${(sol / 1000).toFixed(2)}k`;
  if (sol >= 10) return sol.toFixed(1);
  return sol.toFixed(2);
}

export function MilestoneToast(): JSX.Element | null {
  const [toasts, setToasts] = useState<ActiveToast[]>([]);

  useEffect(() => {
    return milestoneEvents.subscribe((e) => {
      setToasts((prev) => [...prev, { ...e, expiresAt: Date.now() + TOAST_MS }]);
    });
  }, []);

  useEffect(() => {
    if (toasts.length === 0) return;
    const t = setInterval(() => {
      const now = Date.now();
      setToasts((prev) => prev.filter((x) => x.expiresAt > now));
    }, 250);
    return () => clearInterval(t);
  }, [toasts.length]);

  if (toasts.length === 0) return null;

  return (
    <div style={containerStyle} role="status" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} style={toastStyle}>
          <div style={badgeStyle}>MILESTONE #{t.id}</div>
          <div style={lineOneStyle}>
            Mcap crossed {fmtSol(t.thresholdLamports)} SOL
          </div>
          <div style={lineTwoStyle}>
            {fmtSol(t.payoutLamports)} SOL paid
            {t.winners.length > 0 ? ` across ${t.winners.length} winners` : ""}
          </div>
        </div>
      ))}
    </div>
  );
}

const containerStyle: React.CSSProperties = {
  position: "fixed",
  top: 12,
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  flexDirection: "column",
  gap: 8,
  zIndex: 1100,
  pointerEvents: "none",
};

const toastStyle: React.CSSProperties = {
  minWidth: 260,
  padding: "10px 14px",
  background: "linear-gradient(135deg, rgba(28,36,51,0.96), rgba(8,8,13,0.96))",
  border: "1px solid #4a8eff",
  borderRadius: 8,
  color: "#e8e8ef",
  fontFamily:
    'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
  boxShadow: "0 10px 30px rgba(0,0,0,0.5), 0 0 0 1px rgba(74,142,255,0.2)",
  animation: "milestoneToastIn 240ms ease-out",
};

const badgeStyle: React.CSSProperties = {
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: 1.4,
  color: "#4a8eff",
};

const lineOneStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 700,
  marginTop: 2,
  fontVariantNumeric: "tabular-nums",
};

const lineTwoStyle: React.CSSProperties = {
  fontSize: 11,
  color: "#9aa0b4",
  marginTop: 1,
  fontVariantNumeric: "tabular-nums",
};
